import React from 'react'
import {FormattedMessage} from 'react-intl'
import {Stack, StackProps, Text, TextProps, useMultiStyleConfig, Grid, GridItem, GridProps, GridItemProps, BoxProps} from '@chakra-ui/react'
import {NewsletterInput, Accordion, AccordionItem, Icon} from '_uiComponents'
import {FooterColumn, FooterCopyright, FooterCopyrightText2, FooterSocial, FooterSocials, Page, LinkObject} from '_types'
import FooterColumnComponent from './partials/footer-column'
import FooterSocialsComponent from './partials/footer-socials'
import FooterCopyrightComponent from './partials/footer-copyright'
import {useViewport, usePageDesigner} from '_customHooks'
import {createRedirectUrl} from '_utils'

/**
 * @name Footer
 *
 * @component
 *
 * @description
 * Renders the newsletter, a list of sections and a copyright text.
 *
 * @remarks
 * The content is retrieved via page designer, see the mapping function below for more informations.
 *
 * @returns {React.FC} - Rendered footer component.
 */
const Footer: React.FC = () => {
    const styles = useMultiStyleConfig('Footer') as Record<string, StackProps & TextProps & GridProps & GridItemProps & BoxProps>
    const {isMobile} = useViewport()

    const {pageComponents, linkObject} = usePageDesigner({pageId: 'footer-pwa'})
    const footerData = mapPageDesignerData(linkObject, pageComponents)

    return (
        <Stack {...styles.footer} as="footer" data-testid="footer">
            <Stack {...styles.main} data-testid="main">
                <Stack {...styles.newsletterContainer}>
                    <Text as="h2" {...styles.sectionTitle}>
                        <FormattedMessage id="label.newsletter" defaultMessage="Newsletter" />
                    </Text>
                    <NewsletterInput analyticsLabel="footer" />
                </Stack>

                {isMobile && (
                    <Accordion allowMultiple variant="footer">
                        {footerData.columns.map((column: FooterColumn) => (
                            <AccordionItem
                                title={
                                    <Text as="h2" {...styles.sectionTitle}>
                                        {column.title}
                                    </Text>
                                }
                                key={column.title}
                                expandIcon={<Icon name="IconPlus" size="xs" />}
                                collapseIcon={<Icon name="IconMinus" size="xs" />}
                            >
                                <FooterColumnComponent title={column.title} links={column.links} isAccordion={true} />
                            </AccordionItem>
                        ))}
                    </Accordion>
                )}

                {!isMobile && (
                    <Grid {...styles.sectionsGrid} data-testid="grid">
                        {footerData.columns.map((column: FooterColumn) => (
                            <GridItem key={column.title} {...styles.sectionsGridItem}>
                                <FooterColumnComponent title={column.title} links={column.links} isAccordion={false} />
                            </GridItem>
                        ))}

                        <GridItem {...styles.sectionsGridItem}>
                            {footerData.socials && <FooterSocialsComponent title={footerData.socials.title} list={footerData.socials.list} />}
                        </GridItem>
                    </Grid>
                )}
            </Stack>

            <Stack {...styles.bottom} data-testid="bottom">
                {footerData.socials && isMobile && <FooterSocialsComponent title={footerData.socials.title} list={footerData.socials.list} />}

                {footerData.legal && (
                    <FooterCopyrightComponent text1={`${new Date().getFullYear()} ${footerData.legal.text1}`} text2={footerData.legal.text2} />
                )}
            </Stack>
        </Stack>
    )
}

/**
 * Maps the page designer data.
 *
 * @param {LinkObject} linkObject
 * @param {Page} [pageComponents] - The components of the page.
 * @returns {Object} obj An object containing the footer columns, footer copyright, and footer socials.
 * @returns {Array<FooterColumn>} obj.columns - The footer columns.
 * @returns {FooterCopyright} [obj.legal] - The footer copyright.
 * @returns {FooterSocials} [obj.socials] - The footer socials.
 */
const mapPageDesignerData = (
    linkObject: LinkObject,
    pageComponents?: Page,
): {columns: Array<FooterColumn>; legal?: FooterCopyright; socials?: FooterSocials} => {
    const columnsData: Array<FooterColumn> = []
    let socialsData: FooterSocials | undefined
    let legalData: FooterCopyright | undefined

    const components = pageComponents?.regions?.[0]?.components

    if (components) {
        // Columns
        components.slice(0, 3).forEach((component) => {
            if (component?.data?.title)
                columnsData.push({
                    title: component?.data?.title,
                    links:
                        component?.regions?.[0]?.components
                            ?.filter((component) => component.data?.title)
                            ?.map((component) => ({
                                id: component.data?.analytics_id,
                                name: component.data?.title,
                                href: createRedirectUrl(component.data?.link, linkObject.hostlink, linkObject.locale, linkObject.site),
                                isExternal: component.data?.target === '_blank',
                                ariaLabel: component.data?.aria_label,
                            })) ?? [],
                })
        })

        // Socials
        const socials = components[3]
        const socialsList: Array<FooterSocial> = []

        socials?.regions?.[0]?.components?.forEach((component) => {
            if (component.data?.title)
                socialsList.push({
                    id: component.data?.analytics_id,
                    name: component.data?.title,
                    href: createRedirectUrl(component.data?.link, linkObject.hostlink, linkObject.locale, linkObject.site),
                })
        })

        socialsData = {
            title: socials?.data?.title,
            list: socialsList,
        }

        // Legal
        const legal = components[4]
        const legalComponents = legal?.regions?.[0]?.components
        const legalText2: FooterCopyrightText2 = {}

        if (legalComponents?.[0]?.data?.title) {
            legalText2.link1 = {
                name: legalComponents?.[0]?.data?.title,
                href: createRedirectUrl(legalComponents?.[0]?.data?.link, linkObject.hostlink, linkObject.locale, linkObject.site),
            }
        }

        if (legalComponents?.[1]?.data?.title) {
            legalText2.link2 = {
                name: legalComponents?.[1]?.data?.title,
                href: createRedirectUrl(legalComponents?.[1]?.data?.link, linkObject.hostlink, linkObject.locale, linkObject.site),
            }
        }

        legalData = {
            text1: legal?.data?.title,
            text2: legalText2.link1 || legalText2.link2 ? legalText2 : undefined,
        }
    }

    return {
        columns: columnsData,
        legal: legalData,
        socials: socialsData,
    }
}

export {Footer}
